import React, { useState, useMemo } from "react";
import { useScanData } from "./hooks/useScanData";
import RegimeBanner from "./components/RegimeBanner";
import FilterBar, { GROUPS } from "./components/FilterBar";
import StockRow from "./components/StockRow";
import StatusBadge from "./components/StatusBadge";

const CAPITAL = 5000000; // ₹50 lakhs default — user can change in config

const TABLE_HEADERS = [
  { label: "Symbol",   align: "left"  },
  { label: "Status",   align: "left"  },
  { label: "Score",    align: "left"  },
  { label: "Close",    align: "right" },
  { label: "Entry",    align: "right" },
  { label: "SL",       align: "right" },
  { label: "T1",       align: "right" },
  { label: "T2",       align: "right" },
  { label: "RR",       align: "right" },
  { label: "Weekly",   align: "center"},
];

const DEFAULT_FILTERS = {
  search: "",
  statusGroup: "All",
  minScore: 0,
  maxScore: 100,
  nr7Only: false,
  ibOnly: false,
};

export default function App() {
  const [market, setMarket] = useState("NSE");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState("raw_score");
  const [sortDir, setSortDir] = useState("desc");

  const { stocks, regime, loading, error, lastUpdated, scanDate } = useScanData(market);

  // Apply filters
  const filtered = useMemo(() => {
    let out = [...stocks];

    if (filters.search) {
      out = out.filter(s => s.Symbol?.includes(filters.search));
    }
    if (filters.statusGroup !== "All") {
      const allowed = GROUPS[filters.statusGroup] || [];
      out = out.filter(s => allowed.includes(s.Status));
    }
    if (filters.minScore > 0) {
      out = out.filter(s => (s.Score || 0) >= filters.minScore);
    }
    if (filters.maxScore < 100) {
      out = out.filter(s => (s.Score || 0) <= filters.maxScore);
    }
    if (filters.nr7Only) {
      out = out.filter(s => s.NR7 === true || s.NR7 === "True");
    }
    if (filters.ibOnly) {
      out = out.filter(s => s.Inside_Bar === true || s.Inside_Bar === "True");
    }

    // Sort
    out.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return out;
  }, [stocks, filters, sortKey, sortDir]);

  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  }

  // Regime data
  const regimeStr = regime?.regime || (stocks[0]?.Regime) || "UNKNOWN";
  const breadth = useMemo(() => {
    if (!stocks.length) return null;
    const above = stocks.filter(s => s.Score > 0).length;
    return above / stocks.length;
  }, [stocks]);

  // Top buy signals
  const BUY_STATUSES = ["FRESH_BUY_SIGNAL", "NEAR_BREAKOUT", "PULLBACK_BUY_ZONE", "BUY_CANDIDATE"];
  const topBuys = stocks.filter(s => BUY_STATUSES.includes(s.Status)).slice(0, 5);

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", background: "#f9fafb", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{
        background: "white", borderBottom: "1px solid #e5e7eb",
        padding: "0 24px", display: "flex", alignItems: "center",
        justifyContent: "space-between", height: 52, position: "sticky", top: 0, zIndex: 100
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#111827", letterSpacing: "-0.02em" }}>
            Champion Trader
          </span>
          <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>Scanner</span>
        </div>

        {/* Market toggle */}
        <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 6, padding: 3, gap: 2 }}>
          {["NSE", "US"].map(m => (
            <button key={m} onClick={() => setMarket(m)} style={{
              padding: "4px 14px", borderRadius: 4, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600,
              background: market === m ? "white" : "transparent",
              color: market === m ? "#111827" : "#9ca3af",
              boxShadow: market === m ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
            }}>{m}</button>
          ))}
        </div>

        <div style={{ fontSize: 12, color: "#9ca3af" }}>
          {scanDate}
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "20px 16px" }}>

        {/* Regime banner */}
        {!loading && (
          <RegimeBanner
            regime={regimeStr}
            breadth={breadth}
            stockCount={stocks.length}
            lastUpdated={lastUpdated}
          />
        )}

        {/* Top buy signals summary */}
        {topBuys.length > 0 && (
          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0",
            borderRadius: 8, padding: "12px 16px", marginBottom: 16
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Today's Top Buy Signals
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {topBuys.map(s => (
                <div key={s.Symbol} style={{
                  background: "white", border: "1px solid #bbf7d0",
                  borderRadius: 6, padding: "6px 12px", fontSize: 12
                }}>
                  <span style={{ fontWeight: 700, marginRight: 6 }}>{s.Symbol}</span>
                  <StatusBadge status={s.Status} />
                  <span style={{ marginLeft: 8, color: "#6b7280" }}>Score {Math.round(s.Score)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No data state */}
        {!loading && stocks.length === 0 && !error && (
          <div style={{
            background: "white", border: "1px solid #e5e7eb", borderRadius: 8,
            padding: 48, textAlign: "center"
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
            <div style={{ fontWeight: 600, color: "#374151", marginBottom: 6 }}>No scan data for today yet</div>
            <div style={{ fontSize: 13, color: "#9ca3af" }}>
              The scanner runs automatically at 4:15 PM IST (NSE) and 4:30 PM EST (US) on weekdays.
              <br />Run it manually from GitHub Actions or your laptop to see results.
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
            padding: 16, marginBottom: 16, fontSize: 13, color: "#dc2626"
          }}>
            <strong>Could not load data:</strong> {error}
            <br />
            <span style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, display: "block" }}>
              Make sure Firebase is configured and the scanner has run today.
            </span>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: "center", padding: 48, color: "#9ca3af", fontSize: 14 }}>
            Loading scan data...
          </div>
        )}

        {/* Main table */}
        {!loading && stocks.length > 0 && (
          <>
            <FilterBar
              filters={filters}
              onChange={setFilters}
              totalCount={stocks.length}
              filteredCount={filtered.length}
            />

            <div style={{
              background: "white", border: "1px solid #e5e7eb",
              borderRadius: 8, overflow: "hidden"
            }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                      {TABLE_HEADERS.map((h, i) => (
                        <th
                          key={h.label}
                          onClick={() => handleSort(
                            ["Symbol","Status","Score","Close","Entry","SL","T1","T2","RR_Ratio","Weekly_Uptrend"][i]
                          )}
                          style={{
                            padding: "8px 12px", textAlign: h.align,
                            fontSize: 11, fontWeight: 600, color: "#6b7280",
                            textTransform: "uppercase", letterSpacing: "0.04em",
                            cursor: "pointer", userSelect: "none", whiteSpace: "nowrap"
                          }}
                        >
                          {h.label}
                          {sortKey === ["Symbol","Status","Score","Close","Entry","SL","T1","T2","RR_Ratio","Weekly_Uptrend"][i]
                            ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ padding: 32, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                          No stocks match the current filters.
                        </td>
                      </tr>
                    ) : (
                      filtered.map(stock => (
                        <StockRow key={stock.Symbol} stock={stock} capital={CAPITAL} />
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div style={{
                padding: "10px 16px", borderTop: "1px solid #f3f4f6",
                fontSize: 11, color: "#9ca3af", display: "flex",
                justifyContent: "space-between"
              }}>
                <span>Click any row to expand details — Entry, SL, targets, indicators, reason</span>
                <span>Champion Trader Swing System v5.1</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
